import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { User } from '../../services/authService';
import { Search, Shield, AlertTriangle, CheckCircle, MoreVertical, Trash2, Key, Gift, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<'all' | 'suspended' | 'active'>('all');
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ userId: string; userName: string; typed: string } | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        loadUsers();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClick = () => setOpenMenuId(null);
        if (openMenuId) window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, [openMenuId]);

    const loadUsers = async () => {
        setLoading(true);
        const allUsers = await adminService.getAllUsers();
        setUsers(allUsers);
        setLoading(false);
    };

    const handleSuspend = async (userId: string) => {
        const reason = window.prompt("Enter reason for suspension:");
        if (!reason) return;
        setActionLoading(userId);
        try {
            await adminService.suspendUser(userId, reason);
            toast.success('User suspended');
            loadUsers();
        } catch (e) {
            toast.error('Failed to suspend user');
        }
        setActionLoading(null);
    };

    const handleUnsuspend = async (userId: string) => {
        setActionLoading(userId);
        try {
            await adminService.unsuspendUser(userId);
            toast.success('User restored');
            loadUsers();
        } catch (e) {
            toast.error('Failed to unsuspend');
        }
        setActionLoading(null);
    };

    const handleGrantSub = async (userId: string, type: '7_day' | '30_day' | 'lifetime') => {
        setActionLoading(userId);
        try {
            await adminService.grantSubscription(userId, type);
            toast.success('Subscription granted');
            loadUsers();
        } catch (e) {
            toast.error('Failed to grant subscription');
        }
        setActionLoading(null);
    };

    const handleGrantFree = async (userId: string) => {
        setActionLoading(userId);
        try {
            await adminService.grantFreeAccount(userId);
            toast.success('Free account granted');
            loadUsers();
        } catch (e) {
            toast.error('Failed to grant free account');
        }
        setActionLoading(null);
    };

    const handlePasswordReset = async (email: string) => {
        setActionLoading(email);
        try {
            await adminService.sendPasswordReset(email);
            toast.success(`Password reset sent to ${email}`);
        } catch (e) {
            toast.error('Failed to send password reset');
        }
        setActionLoading(null);
    };

    const handleDeleteUser = async () => {
        if (!deleteConfirm) return;
        if (deleteConfirm.typed !== 'DELETE') return;

        setActionLoading(deleteConfirm.userId);
        try {
            await adminService.deleteUser(deleteConfirm.userId);
            toast.success('User permanently deleted');
            setDeleteConfirm(null);
            loadUsers();
        } catch (e) {
            toast.error('Failed to delete user');
        }
        setActionLoading(null);
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '—';
        const d = typeof timestamp === 'number' ? new Date(timestamp) : timestamp.toDate?.() || new Date(timestamp);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filter === 'all' ? true :
            filter === 'suspended' ? user.isSuspended :
                !user.isSuspended;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 border-l-4 border-red-500 pl-3">User Management</h1>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{users.length} users</span>
                    <div className="flex gap-1">
                        {(['all', 'active', 'suspended'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1 rounded text-sm capitalize ${filter === f
                                    ? f === 'suspended' ? 'bg-red-600 text-white' : f === 'active' ? 'bg-green-600 text-white' : 'bg-slate-800 text-white'
                                    : 'bg-white text-slate-600 border'
                                    }`}
                            >{f}</button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center gap-3">
                <Search className="text-gray-400" size={18} />
                <input
                    type="text"
                    placeholder="Search users by name, email..."
                    className="flex-1 outline-none text-gray-700"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-semibold border-b">
                        <tr>
                            <th className="px-6 py-3">User</th>
                            <th className="px-6 py-3">Status</th>
                            <th className="px-6 py-3">Subscription</th>
                            <th className="px-6 py-3">Joined</th>
                            <th className="px-6 py-3">Role</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr><td colSpan={6} className="p-8 text-center text-gray-500">
                                <Loader2 className="animate-spin mx-auto mb-2" size={20} />Loading users...
                            </td></tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr><td colSpan={6} className="p-8 text-center text-gray-500">No users found.</td></tr>
                        ) : (
                            filteredUsers.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900">{user.name}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.isSuspended ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-semibold">
                                                <AlertTriangle size={12} /> Suspended
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-semibold">
                                                <CheckCircle size={12} /> Active
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm">
                                            {(() => {
                                                const status = user.subscriptionStatus;
                                                const type = user.subscriptionType;
                                                const isExpired = user.subscriptionExpiry ? user.subscriptionExpiry < Date.now() : false;

                                                if (isExpired && status !== 'active') {
                                                    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Expired</span>;
                                                }
                                                if (status === 'trial') {
                                                    const daysLeft = user.subscriptionExpiry ? Math.max(0, Math.ceil((user.subscriptionExpiry - Date.now()) / (24 * 60 * 60 * 1000))) : 0;
                                                    return (
                                                        <div>
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Trial</span>
                                                            <div className="text-xs text-gray-400 mt-0.5">{daysLeft}d remaining</div>
                                                        </div>
                                                    );
                                                }
                                                if (status === 'active' && type === 'gift') {
                                                    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">{user.subscriptionLabel || 'Free'}</span>;
                                                }
                                                if (status === 'active' && type === 'paid') {
                                                    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Paid</span>;
                                                }
                                                if (status === 'active') {
                                                    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">{user.subscriptionLabel || 'Active'}</span>;
                                                }
                                                if (status === 'expired') {
                                                    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Canceled</span>;
                                                }
                                                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">No Plan</span>;
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {formatDate(user.createdAt)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.role === 'admin' ? (
                                            <span className="flex items-center gap-1 text-purple-600 text-xs font-bold uppercase tracking-wider">
                                                <Shield size={12} /> Admin
                                            </span>
                                        ) : (
                                            <span className="text-gray-500 text-xs">User</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="relative inline-block">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuId(openMenuId === user.id ? null : user.id);
                                                }}
                                                className="p-2 hover:bg-gray-100 rounded text-gray-500"
                                                disabled={actionLoading === user.id}
                                            >
                                                {actionLoading === user.id ? (
                                                    <Loader2 size={16} className="animate-spin" />
                                                ) : (
                                                    <MoreVertical size={16} />
                                                )}
                                            </button>

                                            {openMenuId === user.id && (
                                                <div
                                                    className="absolute right-0 top-full mt-1 w-56 bg-white shadow-xl rounded-lg border border-gray-100 z-50 py-1"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {/* Subscription Section */}
                                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Access Control</div>
                                                    <button onClick={() => { handleGrantSub(user.id, '30_day'); setOpenMenuId(null); }}
                                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2">
                                                        <Clock size={14} className="text-blue-500" /> Grant 30 Day trial
                                                    </button>
                                                    <button onClick={() => { handleGrantSub(user.id, 'lifetime'); setOpenMenuId(null); }}
                                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-indigo-600 font-medium flex items-center gap-2">
                                                        <Crown size={14} className="text-yellow-600" /> Grant Lifetime
                                                    </button>
                                                    <button onClick={() => { handleGrantFree(user.id); setOpenMenuId(null); }}
                                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-green-600 font-medium border-t border-gray-50">
                                                        <CheckCircle size={14} className="text-green-600" /> Mark as Free (No Trial)
                                                    </button>

                                                    <div className="border-t border-gray-100 my-1" />

                                                    {/* Account Section */}
                                                    <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Account</div>
                                                    <button onClick={() => { handlePasswordReset(user.email); setOpenMenuId(null); }}
                                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700">
                                                        <Key size={14} /> Send Password Reset
                                                    </button>

                                                    {user.isSuspended ? (
                                                        <button onClick={() => { handleUnsuspend(user.id); setOpenMenuId(null); }}
                                                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-green-600">
                                                            Unsuspend
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => { handleSuspend(user.id); setOpenMenuId(null); }}
                                                            disabled={user.role === 'admin'}
                                                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-orange-600 disabled:opacity-50">
                                                            Suspend User
                                                        </button>
                                                    )}

                                                    <div className="border-t border-gray-100 my-1" />

                                                    {/* Danger Zone */}
                                                    <button
                                                        onClick={() => {
                                                            setDeleteConfirm({ userId: user.id, userName: user.name, typed: '' });
                                                            setOpenMenuId(null);
                                                        }}
                                                        disabled={user.role === 'admin'}
                                                        className="w-full text-left px-3 py-2 hover:bg-red-50 text-sm flex items-center gap-2 text-red-600 disabled:opacity-50"
                                                    >
                                                        <Trash2 size={14} /> Delete User
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <Trash2 className="text-red-600" size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">Delete User</h3>
                                <p className="text-sm text-gray-500">This action cannot be undone.</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            You are about to permanently delete <strong>{deleteConfirm.userName}</strong>.
                            This will remove their authentication account and all Firestore data.
                        </p>

                        <p className="text-sm text-gray-600 mb-2">
                            Type <strong className="text-red-600">DELETE</strong> to confirm:
                        </p>
                        <input
                            type="text"
                            value={deleteConfirm.typed}
                            onChange={(e) => setDeleteConfirm({ ...deleteConfirm, typed: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
                            placeholder="Type DELETE"
                        />

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                disabled={deleteConfirm.typed !== 'DELETE' || !!actionLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
